import { Position } from '@/core/collections/positions/position.interface';
import { StoreState } from '@/core/models/store.interface';
import { LocationQuery } from 'vue-router';
import { Module } from 'vuex';
import { HitStatusTypeKey } from '../hit-status-types/hit-status-type.interface';
import { HitStatus } from '../hit-status/hit-status.interface';
import { Hit } from '../hits/hit.interface';
import { User } from '../users/user.interface';
import { ApiResource } from './../../models/api-resource.interface';
import { Candidate } from './../candidates/candidate.interface';
import { Customer } from './../customers/customer.interface';
import { FollowUp } from './follow-up.interface';
import { FollowUpService } from './follow-up.service';
const service = new FollowUpService();

export interface FollowUpState {
   followUpOverview?: ApiResource<Array<FollowUp>>;
   query?: LocationQuery;
   filterData: { [key: string]: Customer | Position | Candidate | number };
   activeIndex: number;
}

const FollowUpStore: Module<FollowUpState, StoreState> = {
   namespaced: true,
   state: {
      filterData: {},
      activeIndex: 0,
   },
   getters: {},
   mutations: {
      SET_FOLLOW_UP_OVERVIEW(state, followUpOverview: ApiResource<Array<any>>) {
         state.followUpOverview = followUpOverview;
      },
      DELETE_HIT_FROM_FOLLOW_UP(state, { hitId, hitGroupName }: { hitId: number; hitGroupName: string }) {
         const data = state.followUpOverview?.data || [];
         const groupIndex = data.findIndex((c) => c.group_name === hitGroupName);
         data[groupIndex].hits = data[groupIndex].hits.filter((h) => h.id !== hitId);
      },
      UPDATE_HIT_USER(state, { hitId, user }: { hitId: number; user?: User | null }) {
         const data = state.followUpOverview?.data || [];
         const hit = data.flatMap((group) => group.hits).find((existingHit) => existingHit.id === hitId);
         if (!hit) return;

         hit.user = user ?? undefined;
         hit.user_id = user?.id ?? null;
      },
      UPDATE_HIT_STATUS(state, payload: { hitStatus: HitStatus; hitGroupName: string; showHiddenStatuses: boolean; user?: User }) {
         const { hitStatus, hitGroupName } = payload;
         if (!hitStatus) return;

         const data = state.followUpOverview?.data || [];
         const groupIndex = data.findIndex((c) => c.group_name === hitGroupName);
         const hitIndex = data[groupIndex]?.hits.findIndex((h) => h.id === hitStatus.hit_id);
         const existingHit = data[groupIndex]?.hits[hitIndex];

         if (!existingHit) return;

         // hitStatus.user is the status creator, not necessarily the hit's assignee
         const user = payload.user ?? hitStatus.hit?.user ?? existingHit.user;
         const hit = { ...existingHit, status: hitStatus, user, user_id: user?.id ?? existingHit.user_id };

         const isHiddenStatus =
            (hitStatus.hit_status_type?.key === HitStatusTypeKey.Closed || hitStatus.hit_status_type?.key === HitStatusTypeKey.Starter) &&
            !payload.showHiddenStatuses;
         const newGroupIndex = data.findIndex((c) => c.group_name === hitStatus.hit_status_type?.name);

         if (newGroupIndex === groupIndex && !isHiddenStatus) {
            data[groupIndex].hits[hitIndex] = hit;
            return;
         }

         data[groupIndex].hits.splice(hitIndex, 1);
         if (isHiddenStatus) return;

         if (newGroupIndex !== -1) {
            data[newGroupIndex].hits.push(hit);
         } else {
            if (hitStatus.hit_status_type?.name) {
               data.push({ group_name: hitStatus.hit_status_type?.name, hits: [hit] });
            }
         }

         //#region set activeIndex
         const allHits = (state.followUpOverview?.data || []).reduce((acc: any, cur: FollowUp) => acc.concat(cur.hits), [] as Array<Hit>);
         state.activeIndex = allHits.findIndex((h: Hit) => h.id === hit.id);
         //#endregion
      },
      UPDATE_CANDIDATE(state, { candidate }: { candidate: Candidate }) {
         // update all candidates in followUpOverview
         const data = state.followUpOverview?.data || [];
         data.forEach((group) => {
            group.hits.forEach((hit) => {
               if (hit.candidate_id === candidate.id) {
                  hit.candidate = { ...candidate, loaded: true };
               }
            });
         });
      },
      DELETE_CANDIDATE(state, { candidateId }: { candidateId: number }) {
         const data = state.followUpOverview?.data || [];
         data.forEach((group) => {
            group.hits = group.hits.filter((h) => h.candidate?.id !== candidateId);
         });
      },
      UPDATE_CUSTOMER(state, { customer }: { customer: Customer }) {
         // update all customers in followUpOverview
         const data = state.followUpOverview?.data || [];
         data.forEach((group) => {
            group.hits.forEach((hit) => {
               if (hit.customer_id === customer.id) {
                  hit.customer = { ...customer, loaded: true };
               }
            });
         });
      },
      UPDATE_POSITION(state, { position }: { position: Position }) {
         // update all positions in followUpOverview
         const data = state.followUpOverview?.data || [];
         data.forEach((group) => {
            group.hits.forEach((hit) => {
               if (hit.position_id === position.id) {
                  hit.position = { ...position, loaded: true };
               }
            });
         });
      },
      DELETE_POSITION(state, { positionId }: { positionId: number }) {
         const data = state.followUpOverview?.data || [];
         data.forEach((group) => {
            group.hits = group.hits.filter((h) => h.position?.id !== positionId);
         });
      },
      SET_QUERY(state, query?: LocationQuery) {
         state.query = query;
      },
      SET_FILTER_DATA(state, { key, value }: { key: string; value: Customer | Position | Candidate }) {
         state.filterData[key] = value;
      },
      RESET_FILTER_DATA(state) {
         state.filterData = {};
      },
   },
   actions: {
      async getFollowUpData(context, query: LocationQuery) {
         return await service.getFollowUpData(query);
      },
   },
};

export default FollowUpStore;
